// Based on the [postcss-pxtorem plugin](https://github.com/cuth/postcss-pxtorem)

import { DECLARATION, Element } from 'stylis'

const minPixelValue = 4
export const ROOT_VALUE = 16
const unitPrecision = 5

// See https://github.com/cuth/postcss-pxtorem/blob/master/lib/pixel-unit-regex.js
const pxRegExp = /"[^"]+"|'[^']+'|url\([^)]+\)|(\d*\.?\d+)px/gi

// Not an exhaustive list but should cover most of our use cases:
// https://developer.mozilla.org/en-US/docs/Web/CSS/Reference
const blocklist = [
  'column-count',
  'columnCount',
  'counter-increment',
  'counter-reset',
  'counterIncrement',
  'counterReset',
  'flex',
  'flex-grow',
  'flex-order',
  'flex-shrink',
  'flexGrow',
  'flexOrder',
  'flexShrink',
  'font-weight',
  'fontWeight',
  'opacity',
  'order',
  'stroke-miterlimit',
  'stroke-width',
  'strokeMiterlimit',
  'strokeWidth',
  'z-index',
  'zIndex',
]

const isNumberPropBlocklisted = (prop) =>
  blocklist.some((property) => property === prop)

const isValueInRange = (value) => Math.abs(value) > minPixelValue

const toFixed = (num, precision) => {
  const multiplier = 10 ** (precision + 1)
  const wholeNumber = Math.floor(num * multiplier)
  return (Math.round(wholeNumber / 10) * 10) / multiplier
}

const toRem = (value) => {
  const fixed = toFixed(Number(value) / ROOT_VALUE, unitPrecision)
  return fixed === 0 ? '0' : `${fixed}rem`
}

const pixelReplace = () => (pxValue, numValue) => {
  if (!numValue) {
    return pxValue
  }
  if (!isValueInRange(numValue)) {
    return pxValue
  }
  return toRem(numValue)
}

const px2rem = (element: Element) => {
  if (element.type !== DECLARATION) {
    return
  }

  // children is the declaration value i.e. #FFF
  // props is the declaration property i.e. backgroundColor
  // value is a combination of the two with semi at end i.e. backgroundColor: #FFF;
  const { children, props, value } = element
  const numValue = Number(children)

  /** Logic breakdown:
  // If we add `return` to the element value, it will override the rendered style value
  // For more info, see https://github.com/thysultan/stylis#prefixing
  **/
  if (/\s?!px/.test(value)) {
    element.return = value.replace(/\s?!px/, '')
    return
  }

  if (
    !Number.isNaN(numValue) &&
    isValueInRange(numValue) &&
    !isNumberPropBlocklisted(props)
  ) {
    element.return = `${props}:${toRem(numValue)};`
    return
  }

  if (typeof value === 'string' && value.includes('px')) {
    element.return = value.replace(pxRegExp, pixelReplace())
  }
}

export default px2rem
